// src/screens/Settings.js
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  User,
  Users,
  CreditCard,
  Bell,
  Palette,
  ShieldCheck,
  Save,
  Download,
  Trash2,
  Upload,
  AlertTriangle,
  Zap,
  TrendingUp,
  Activity,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Link2,
} from 'lucide-react';
import './Settings.css';
import { getApiUrl } from '../../config/api';

// ── Helpers ────────────────────────────────────────────────────────────────
const getToken   = () => localStorage.getItem('access_token') || '';
const getUserId  = () => localStorage.getItem('user_id')      || '';
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });


const Settings = () => {
  const [activeSection,      setActiveSection]      = useState('Account');

  // ── Account form state ─────────────────────────────────────────────────
  const [form,        setForm]        = useState({
    first_name: '',
    last_name:  '',
    email:      '',
    mobile:     '',
    company:    '',
    timezone:   '',
    site_url:   '',
  });
  const [isFetching,  setIsFetching]  = useState(false);
  const [isSaving,    setIsSaving]    = useState(false);
  const [fetchError,  setFetchError]  = useState('');
  const [saveStatus,  setSaveStatus]  = useState(''); // 'success' | 'error' | ''
  const [saveMsg,     setSaveMsg]     = useState('');

  // ── Fetch user on mount ────────────────────────────────────────────────
  const fetchUser = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    setIsFetching(true);
    setFetchError('');
    try {
      const { data } = await axios.get(getApiUrl(`/users/${userId}`), {
        headers: { ...authHeader(), Accept: 'application/json' },
      });
      setForm({
        first_name: data.first_name || '',
        last_name:  data.last_name  || '',
        email:      data.email      || '',
        mobile:     data.mobile     || '',
        company:    data.company    || '',
        timezone:   data.timezone   || Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        site_url:   data.site_url   || '',
      });
    } catch (err) {
      setFetchError(
        err.response?.data?.detail ||
        err.message ||
        'Failed to load account data.'
      );
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // ── Field change handler ───────────────────────────────────────────────
  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setSaveStatus('');
  };

  // ── Save (PUT) ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    const userId = getUserId();
    if (!userId) return;

    setIsSaving(true);
    setSaveStatus('');
    setSaveMsg('');
    try {
      await axios.put(
        getApiUrl(`/users/${userId}`),
        {
          first_name: form.first_name,
          last_name:  form.last_name,
          email:      form.email,
          mobile:     form.mobile,
          company:    form.company,
          timezone:   form.timezone,
          site_url:   form.site_url,
        },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setSaveStatus('success');
      setSaveMsg('Changes saved successfully.');
    } catch (err) {
      setSaveStatus('error');
      setSaveMsg(
        err.response?.data?.detail ||
        err.message ||
        'Failed to save changes.'
      );
    } finally {
      setIsSaving(false);
      // Auto-clear status after 4 seconds
      setTimeout(() => { setSaveStatus(''); setSaveMsg(''); }, 4000);
    }
  };


  // ── Static data (unchanged) ────────────────────────────────────────────
  const navItems = [
    { id: 'Account',       icon: User,        comingSoon: false },
    { id: 'Team',          icon: Users,       comingSoon: true  },
    { id: 'Billing',       icon: CreditCard,  comingSoon: true  },
    { id: 'Notifications', icon: Bell,        comingSoon: true  },
    { id: 'Brand Kit',     icon: Palette,     comingSoon: true  },
    { id: 'Data & Privacy',icon: ShieldCheck, comingSoon: true  },
  ];

  const notificationPrefs = [
    { title: 'Revenue Alerts',        desc: 'Real-time notifications for sudden revenue spikes or drops.',  icon: TrendingUp },
    { title: 'High-Intent Alerts',    desc: 'Get pinged when a high-value customer reaches out.',           icon: Users      },
    { title: 'Automation Summaries',  desc: 'Daily breakdown of AI sequence performance.',                  icon: Zap        },
    { title: 'Weekly Reports',        desc: 'In-depth performance audit sent every Monday.',                icon: Activity   },
  ];

  const privacyControls = [
    { label: 'Anonymous AI Training',  desc: 'Allows Revexia to use de-identified data to improve global prediction models.', enabled: true  },
    { label: 'Strict GDPR Compliance', desc: 'Automatically redacts PII from all DM activity logs after 30 days.',            enabled: false },
    { label: 'Cloud Attribution Sync', desc: 'Securely syncs cross-channel identifiers for deeper revenue mapping.',           enabled: true  },
  ];

  const brandColors = ['#3B82F6', '#A855F7', '#F97316'];


  // ── Section Renderers ──────────────────────────────────────────────────
  const renderContent = () => {
    switch (activeSection) {

      case 'Account':
        return (
          <div className="settings-content-animate">

            {/* Fetch error */}
            {fetchError && (
              <div className="settings-alert settings-alert-error mb-4">
                <AlertCircle size={16} />
                <span>{fetchError}</span>
              </div>
            )}

            {/* Loading skeleton */}
            {isFetching ? (
              <div className="settings-loading">
                <Loader2 size={24} className="settings-spinner" />
                <span>Loading account data…</span>
              </div>
            ) : (
              <>
                <div className="row g-4 mb-5">

                  {/* First Name */}
                  <div className="col-12 col-md-6">
                    <div className="settings-input-group">
                      <label className="settings-input-label">First Name</label>
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={handleFormChange('first_name')}
                        placeholder="Alex"
                        className="settings-input-field"
                      />
                    </div>
                  </div>

                  {/* Last Name */}
                  <div className="col-12 col-md-6">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Last Name</label>
                      <input
                        type="text"
                        value={form.last_name}
                        onChange={handleFormChange('last_name')}
                        placeholder="Chen"
                        className="settings-input-field"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-12 col-md-6">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Primary Email</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={handleFormChange('email')}
                        placeholder="admin@company.com"
                        className="settings-input-field"
                      />
                    </div>
                  </div>

                  {/* Mobile */}
                  {/* <div className="col-12 col-md-6">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Mobile</label>
                      <div className="settings-input-icon-wrapper">
                        <Phone className="settings-input-icon" size={16} />
                        <input
                          type="tel"
                          value={form.mobile}
                          onChange={handleFormChange('mobile')}
                          placeholder="+8801XXXXXXXXX"
                          className="settings-input-field settings-input-with-icon"
                        />
                      </div>
                    </div>
                  </div> */}

                  {/* Company */}
                  <div className="col-12 col-md-6">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Company Name</label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={handleFormChange('company')}
                        placeholder="Acme Inc."
                        className="settings-input-field"
                      />
                    </div>
                  </div>

                  {/* Timezone */}
                  {/* <div className="col-12 col-md-6">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Timezone</label>
                      <div className="settings-select-wrapper">
                        <input
                          type="text"
                          value={form.timezone}
                          onChange={handleFormChange('timezone')}
                          placeholder="Asia/Dhaka"
                          className="settings-input-field"
                        />
                        <Globe className="settings-select-icon" size={18} />
                      </div>
                    </div>
                  </div> */}

                  {/* Store URL — full width */}
                  <div className="col-12">
                    <div className="settings-input-group">
                      <label className="settings-input-label">
                        Store URL
                        <span className="settings-input-hint"> (Shopify / WooCommerce)</span>
                      </label>
                      <div className="settings-input-icon-wrapper">
                        <Link2 className="settings-input-icon" size={16} />
                        <input
                          type="url"
                          value={form.site_url}
                          onChange={handleFormChange('site_url')}
                          placeholder="https://yourstore.myshopify.com"
                          className="settings-input-field settings-input-with-icon"
                        />
                      </div>
                    </div>
                  </div>

                </div>

                {/* Save feedback */}
                {saveStatus === 'success' && (
                  <div className="settings-alert settings-alert-success mb-3">
                    <CheckCircle2 size={16} />
                    <span>{saveMsg}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div className="settings-alert settings-alert-error mb-3">
                    <AlertCircle size={16} />
                    <span>{saveMsg}</span>
                  </div>
                )}

                <div className="settings-actions-bar">
                  <button
                    className="settings-btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? <><Loader2 size={16} className="settings-spinner" /> Saving…</>
                      : <><Save size={16} /> Save Changes</>}
                  </button>
                  <button className="settings-btn-secondary">
                    Reset Password
                  </button>
                </div>
              </>
            )}
          </div>
        );

      case 'Team':
        return (
          <div className="settings-content-animate">
            <div className="settings-section-header">
              <div>
                <h3 className="settings-section-title">Organization Access</h3>
                <p className="settings-section-subtitle">Manage permissions and team roles</p>
              </div>
            </div>
            <div className="settings-empty-card text-center py-5">
              <Users size={36} className="text-muted mb-3" />
              <h4 style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>Team Management Coming Soon</h4>
              <p className="text-muted" style={{ fontSize: '13px', maxWidth: '400px', margin: '8px auto 0' }}>
                Multi-user access control and role management are currently under development.
              </p>
            </div>
          </div>
        );

      case 'Billing':
        return (
          <div className="settings-content-animate">
            <div className="settings-section-header">
              <div>
                <h3 className="settings-section-title">Billing & Subscriptions</h3>
                <p className="settings-section-subtitle">Manage subscription tiers and payment methods</p>
              </div>
            </div>
            <div className="settings-empty-card text-center py-5">
              <CreditCard size={36} className="text-muted mb-3" />
              <h4 style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>Billing Portal Coming Soon</h4>
              <p className="text-muted" style={{ fontSize: '13px', maxWidth: '400px', margin: '8px auto 0' }}>
                Subscription management and automated invoicing will be available shortly.
              </p>
            </div>
          </div>
        );

      case 'Notifications':
        return (
          <div className="settings-content-animate">
            {notificationPrefs.map((pref, i) => (
              <div key={i} className="settings-notification-item">
                <div className="settings-notification-content">
                  <div className="settings-notification-icon"><pref.icon size={20} /></div>
                  <div>
                    <h5 className="settings-notification-title">{pref.title}</h5>
                    <p className="settings-notification-desc">{pref.desc}</p>
                  </div>
                </div>
                <div className="settings-toggle-switch active">
                  <div className="settings-toggle-thumb"></div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'Brand Kit':
        return (
          <div className="settings-content-animate">
            <div className="row g-5">
              <div className="col-12 col-lg-6">
                <div className="mb-5">
                  <h4 className="settings-brand-section-title">Brand Logo</h4>
                  <div className="settings-brand-upload-zone">
                    <div className="settings-brand-upload-icon"><Upload size={32} /></div>
                    <p className="settings-brand-upload-text">Drop logo to upload</p>
                  </div>
                </div>
                <div>
                  <h4 className="settings-brand-section-title">Brand Identity Colors</h4>
                  <div className="row g-4">
                    {brandColors.map((color) => (
                      <div key={color} className="col-4">
                        <div className="settings-brand-color-card">
                          <div className="settings-brand-color-preview" style={{ backgroundColor: color }}></div>
                          <span className="settings-brand-color-code">{color}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="settings-brand-preview-card">
                  <h4 className="settings-brand-preview-title">AI Output Preview</h4>
                  <p className="settings-brand-preview-subtitle">
                    Revexia AI uses these assets to brand all customer-facing offers and follow-ups.
                  </p>
                  <div className="settings-brand-preview-demo">
                    <div className="settings-brand-preview-glow"></div>
                    <div className="settings-brand-preview-header">
                      <div className="settings-brand-preview-logo">R</div>
                      <div>
                        <p className="settings-brand-preview-name">Revexia AI</p>
                        <p className="settings-brand-preview-type">Dynamic Offer</p>
                      </div>
                    </div>
                    <p className="settings-brand-preview-message">
                      "Hey! We noticed you liked our Winter Glow collection..."
                    </p>
                    <button className="settings-brand-preview-cta">Claim 10% Offer</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="settings-actions-bar justify-content-end">
              <button className="settings-btn-secondary">Update Brand Profile</button>
            </div>
          </div>
        );

      case 'Data & Privacy':
        return (
          <div className="settings-content-animate">
            <div className="settings-privacy-warning">
              <div className="settings-privacy-warning-icon"><AlertTriangle size={24} /></div>
              <div>
                <h4 className="settings-privacy-warning-title">Critical Privacy Controls</h4>
                <p className="settings-privacy-warning-text">
                  Adjusting these settings directly impacts how the Revexia engine processes customer signals.
                  Changes may take up to 24 hours to propagate through all AI nodes.
                </p>
              </div>
            </div>
            <div className="settings-privacy-controls">
              {privacyControls.map((item, i) => (
                <div key={i} className="settings-privacy-item">
                  <div>
                    <h5 className="settings-privacy-item-title">{item.label}</h5>
                    <p className="settings-privacy-item-desc">{item.desc}</p>
                  </div>
                  <div className={`settings-toggle-switch ${item.enabled ? 'active' : ''}`}>
                    <div className="settings-toggle-thumb"></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="settings-privacy-actions">
              <button className="settings-btn-secondary">
                <Download size={16} /> Export System Audit
              </button>
              <button className="settings-btn-danger">
                <Trash2 size={16} /> Delete All Analytics
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };


  return (
    <>
      <Helmet>
        <title>Settings - Revexia AI Revenue Brain</title>
        <meta name="description" content="Configure your Revexia ecosystem and platform preferences" />
      </Helmet>

      <div className="settings-page-wrapper">
        <div className="container-fluid settings-container">
          <div className="row settings-main-row">

            {/* Sidebar */}
            <aside className="col-12 col-xl-3 settings-aside">
              <div className="settings-nav-sidebar">
                {navItems.map((item) => (
                  <div key={item.id} className="settings-nav-item-wrapper">
                    <button
                      type="button"
                      onClick={() => !item.comingSoon && setActiveSection(item.id)}
                      className={`settings-nav-item ${activeSection === item.id ? 'active' : ''}${item.comingSoon ? ' settings-nav-item-disabled' : ''}`}
                      disabled={item.comingSoon}
                    >
                      <div className="settings-nav-item-content">
                        <div className="settings-nav-item-icon"><item.icon size={18} /></div>
                        <span className="settings-nav-item-label">{item.id}</span>
                        {item.comingSoon && (
                          <span className="settings-coming-soon-pill">Soon</span>
                        )}
                      </div>
                      {activeSection === item.id && (
                        <div className="settings-nav-item-indicator"></div>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </aside>

            {/* Content */}
            <main className="col-12 col-xl-9 settings-main">
              <div className="settings-content-panel">
                <div className="settings-content-bg-mesh"></div>
                <header className="settings-content-header">
                  <h2 className="settings-content-title">{activeSection} Control</h2>
                  <div className="settings-content-subtitle-wrapper">
                    <div className="settings-content-subtitle-dot"></div>
                    <p className="settings-content-subtitle">Configure your ecosystem behavior and platform trust</p>
                  </div>
                </header>
                {renderContent()}
              </div>
            </main>

          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;